import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { API_URL } from "../config/api";
import {
  Building2,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Receipt,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle,
  XCircle,
  Filter,
  Ban,
  RotateCcw,
  FileX,
} from "lucide-react";


// Color scheme for verticals
const VERTICAL_COLORS = {
  ep_holding: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", badge: "bg-blue-500" },
  logistika: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", badge: "bg-emerald-500" },
  cargo_cfs: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-700", badge: "bg-orange-500" },
  epas: { bg: "bg-pink-50", border: "border-pink-200", text: "text-pink-700", badge: "bg-pink-500" },
  artcom: { bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-700", badge: "bg-purple-500" },
};

export default function ARAPPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({ verticals: [] });
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [viewMode, setViewMode] = useState("total"); // "total" or vertical_id
  const [expandedVerticals, setExpandedVerticals] = useState({});
  const [expandedPartners, setExpandedPartners] = useState({});

  const togglePartner = (key) => {
    setExpandedPartners((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Group entries by partner name (customer_name for AR, vendor_name for AP).
  // Returns an array of { name, vertical_id, count, total, entries[] } sorted
  // by descending |total| so the most exposure rises to the top.
  const groupByPartner = (verticalEntries, nameKey) => {
    const buckets = {};
    Object.entries(verticalEntries || {}).forEach(([verticalId, data]) => {
      (data.entries || []).forEach((entry) => {
        const name = entry[nameKey] || "(unknown)";
        const key = `${verticalId}::${name}`;
        if (!buckets[key]) {
          buckets[key] = { key, name, vertical_id: verticalId, count: 0, total: 0, entries: [] };
        }
        buckets[key].count += 1;
        buckets[key].total += Number(entry.amount || 0) - Number(entry.paid_amount || 0);
        buckets[key].entries.push(entry);
      });
    });
    return Object.values(buckets).sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
  };

  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/ar-ap/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConfig(response.data);
    } catch (error) {
      console.error("Failed to fetch config:", error);
    }
  }, [token]);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const url = viewMode === "total" 
        ? `${API_URL}/ar-ap/dashboard`
        : `${API_URL}/ar-ap/dashboard?vertical_id=${viewMode}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
      toast.error("Failed to load AR/AP data");
    } finally {
      setLoading(false);
    }
  }, [token, viewMode]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const toggleVertical = (verticalId) => {
    setExpandedVerticals((prev) => ({
      ...prev,
      [verticalId]: !prev[verticalId],
    }));
  };

  const formatCurrency = (amount, currency = "EUR") => {
    if (amount === null || amount === undefined) return "-";
    return new Intl.NumberFormat("sl-SI", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getVerticalName = (verticalId) => {
    const v = config.verticals?.find((v) => v.id === verticalId);
    return v?.name || verticalId;
  };

  const getStatusBadge = (status, isWrittenOff = false, isNonPayable = false) => {
    if (isWrittenOff) return "bg-slate-200 text-slate-600 line-through";
    if (isNonPayable) return "bg-slate-200 text-slate-600 line-through";
    const styles = {
      open: "bg-blue-100 text-blue-700",
      partial: "bg-yellow-100 text-yellow-700",
      paid: "bg-green-100 text-green-700",
      overdue: "bg-red-100 text-red-700",
      approved: "bg-emerald-100 text-emerald-700",
      written_off: "bg-slate-200 text-slate-600",
      non_payable: "bg-slate-200 text-slate-600",
      cancelled: "bg-gray-100 text-gray-500",
    };
    return styles[status] || "bg-gray-100 text-gray-700";
  };

  // Write-off and Non-payable handlers
  const handleWriteOff = async (entryId) => {
    const reason = window.prompt("Reason for write-off (optional):");
    if (reason === null) return; // User cancelled
    
    try {
      await axios.post(`${API_URL}/ar-ap/ar/${entryId}/write-off`, 
        { reason: reason || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("AR entry marked as written off");
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to write off entry");
    }
  };

  const handleRestoreAR = async (entryId) => {
    try {
      await axios.post(`${API_URL}/ar-ap/ar/${entryId}/restore`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("AR entry restored");
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to restore entry");
    }
  };

  const handleNonPayable = async (entryId) => {
    const reason = window.prompt("Reason for marking as non-payable (optional):");
    if (reason === null) return; // User cancelled
    
    try {
      await axios.post(`${API_URL}/ar-ap/ap/${entryId}/non-payable`,
        { reason: reason || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("AP entry marked as non-payable");
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to mark as non-payable");
    }
  };

  const handleRestoreAP = async (entryId) => {
    try {
      await axios.post(`${API_URL}/ar-ap/ap/${entryId}/restore`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("AP entry restored");
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to restore entry");
    }
  };

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const arTotals = dashboard?.ar?.totals || { total: 0, open: 0, overdue: 0, paid: 0, count: 0 };
  const apTotals = dashboard?.ap?.totals || { total: 0, open: 0, overdue: 0, paid: 0, count: 0 };
  const netPosition = dashboard?.net_position || 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-3">
                <DollarSign className="h-7 w-7" />
                Accounts Receivable & Payable
              </h1>
              <p className="text-slate-300 text-sm mt-1">
                {viewMode === "total" ? "All Companies - Total View" : `Viewing: ${getVerticalName(viewMode)}`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* View Mode Selector */}
              <Select value={viewMode} onValueChange={setViewMode}>
                <SelectTrigger className="w-[200px] bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Select view" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="total">
                    <span className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Total (All Companies)
                    </span>
                  </SelectItem>
                  {config.verticals?.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button
                size="sm"
                variant="outline"
                onClick={fetchDashboard}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
          {/* AR Card */}
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-green-700 flex items-center gap-2">
                <ArrowUpRight className="h-4 w-4" />
                AR Open
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-700">
                {formatCurrency(arTotals.open)}
              </div>
              <p className="text-xs text-green-600 mt-1">{arTotals.count} invoices</p>
              {arTotals.written_off > 0 && (
                <p className="text-xs text-slate-500 mt-1">
                  <FileX className="h-3 w-3 inline mr-1" />
                  {formatCurrency(arTotals.written_off)} written off
                </p>
              )}
            </CardContent>
          </Card>

          {/* AR Overdue */}
          <Card className={arTotals.overdue > 0 ? "bg-red-50 border-red-200" : ""}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                <Clock className="h-4 w-4 text-red-500" />
                AR Overdue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${arTotals.overdue > 0 ? "text-red-600" : "text-slate-400"}`}>
                {formatCurrency(arTotals.overdue)}
              </div>
            </CardContent>
          </Card>

          {/* AP Card */}
          <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-orange-700 flex items-center gap-2">
                <ArrowDownRight className="h-4 w-4" />
                AP Open
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-700">
                {formatCurrency(apTotals.open)}
              </div>
              <p className="text-xs text-orange-600 mt-1">{apTotals.count} invoices</p>
              {apTotals.non_payable > 0 && (
                <p className="text-xs text-slate-500 mt-1">
                  <Ban className="h-3 w-3 inline mr-1" />
                  {formatCurrency(apTotals.non_payable)} non-payable
                </p>
              )}
            </CardContent>
          </Card>

          {/* AP Overdue */}
          <Card className={apTotals.overdue > 0 ? "bg-red-50 border-red-200" : ""}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-500" />
                AP Overdue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${apTotals.overdue > 0 ? "text-red-600" : "text-slate-400"}`}>
                {formatCurrency(apTotals.overdue)}
              </div>
            </CardContent>
          </Card>

          {/* Net Position */}
          <Card className={netPosition >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                {netPosition >= 0 ? <TrendingUp className="h-4 w-4 text-emerald-500" /> : <TrendingDown className="h-4 w-4 text-red-500" />}
                Net Position
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${netPosition >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {netPosition >= 0 ? "+" : ""}{formatCurrency(netPosition)}
              </div>
              <p className="text-xs text-slate-500 mt-1">AR - AP</p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" className="gap-2">
              <Building2 className="h-4 w-4" />
              By Company
            </TabsTrigger>
            <TabsTrigger value="ar" className="gap-2">
              <Receipt className="h-4 w-4" />
              Receivables (AR)
            </TabsTrigger>
            <TabsTrigger value="ap" className="gap-2">
              <DollarSign className="h-4 w-4" />
              Payables (AP)
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab - By Company */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* AR by Vertical */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-700">
                    <ArrowUpRight className="h-5 w-5" />
                    Accounts Receivable by Company
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {config.verticals?.map((vertical) => {
                      const data = dashboard?.ar?.by_vertical?.[vertical.id];
                      const colors = VERTICAL_COLORS[vertical.id] || VERTICAL_COLORS.ep_holding;
                      const isExpanded = expandedVerticals[`ar_${vertical.id}`];
                      
                      return (
                        <div key={vertical.id} className={`rounded-lg border ${colors.border} ${colors.bg}`}>
                          <div
                            className="p-3 cursor-pointer hover:bg-white/50 transition-colors"
                            onClick={() => toggleVertical(`ar_${vertical.id}`)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                <span className={`font-semibold ${colors.text}`}>{vertical.name}</span>
                                {data?.count > 0 && (
                                  <Badge variant="secondary" className="text-xs">
                                    {data.count}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-right">
                                {data ? (
                                  <>
                                    <div className="font-bold text-green-700">{formatCurrency(data.open)}</div>
                                    {data.overdue > 0 && (
                                      <div className="text-xs text-red-600">Overdue: {formatCurrency(data.overdue)}</div>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-slate-400 text-sm">No data</span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {isExpanded && data?.entries?.length > 0 && (
                            <div className="border-t px-3 pb-3 bg-white/50">
                              <table className="w-full text-sm mt-2">
                                <thead>
                                  <tr className="text-slate-500">
                                    <th className="text-left py-1">Invoice</th>
                                    <th className="text-left py-1">Customer</th>
                                    <th className="text-right py-1">Amount</th>
                                    <th className="text-center py-1">Due</th>
                                    <th className="text-center py-1">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {data.entries.slice(0, 10).map((entry) => {
                                    const isWrittenOff = entry.is_written_off;
                                    return (
                                      <tr key={entry.id} className={`border-t border-slate-100 ${isWrittenOff ? 'opacity-50 bg-slate-50' : ''}`}>
                                        <td className={`py-1 font-mono text-xs ${isWrittenOff ? 'line-through' : ''}`}>{entry.invoice_number}</td>
                                        <td className={`py-1 truncate max-w-[120px] ${isWrittenOff ? 'line-through' : ''}`}>{entry.customer_name}</td>
                                        <td className={`py-1 text-right font-medium ${isWrittenOff ? 'line-through text-slate-400' : ''}`}>{formatCurrency(entry.amount)}</td>
                                        <td className="py-1 text-center text-xs">{entry.due_date}</td>
                                        <td className="py-1 text-center">
                                          {isWrittenOff ? (
                                            <button
                                              onClick={() => handleRestoreAR(entry.id)}
                                              className="text-green-600 hover:text-green-800 text-xs flex items-center gap-1 justify-center"
                                              title="Restore"
                                            >
                                              <RotateCcw className="h-3 w-3" />
                                            </button>
                                          ) : (
                                            <button
                                              onClick={() => handleWriteOff(entry.id)}
                                              className="text-slate-500 hover:text-red-600 text-xs flex items-center gap-1 justify-center"
                                              title="Write off (non-collectable)"
                                            >
                                              <FileX className="h-3 w-3" />
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                              {data.entries.length > 10 && (
                                <p className="text-xs text-center text-slate-500 mt-2">
                                  +{data.entries.length - 10} more entries
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* AP by Vertical */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-700">
                    <ArrowDownRight className="h-5 w-5" />
                    Accounts Payable by Company
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {config.verticals?.map((vertical) => {
                      const data = dashboard?.ap?.by_vertical?.[vertical.id];
                      const colors = VERTICAL_COLORS[vertical.id] || VERTICAL_COLORS.ep_holding;
                      const isExpanded = expandedVerticals[`ap_${vertical.id}`];
                      
                      return (
                        <div key={vertical.id} className={`rounded-lg border ${colors.border} ${colors.bg}`}>
                          <div
                            className="p-3 cursor-pointer hover:bg-white/50 transition-colors"
                            onClick={() => toggleVertical(`ap_${vertical.id}`)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                <span className={`font-semibold ${colors.text}`}>{vertical.name}</span>
                                {data?.count > 0 && (
                                  <Badge variant="secondary" className="text-xs">
                                    {data.count}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-right">
                                {data ? (
                                  <>
                                    <div className="font-bold text-orange-700">{formatCurrency(data.open)}</div>
                                    {data.overdue > 0 && (
                                      <div className="text-xs text-red-600">Overdue: {formatCurrency(data.overdue)}</div>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-slate-400 text-sm">No data</span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {isExpanded && data?.entries?.length > 0 && (
                            <div className="border-t px-3 pb-3 bg-white/50">
                              <table className="w-full text-sm mt-2">
                                <thead>
                                  <tr className="text-slate-500">
                                    <th className="text-left py-1">Invoice</th>
                                    <th className="text-left py-1">Vendor</th>
                                    <th className="text-right py-1">Amount</th>
                                    <th className="text-center py-1">Due</th>
                                    <th className="text-center py-1">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {data.entries.slice(0, 10).map((entry) => {
                                    const isNonPayable = entry.is_non_payable;
                                    return (
                                      <tr key={entry.id} className={`border-t border-slate-100 ${isNonPayable ? 'opacity-50 bg-slate-50' : ''}`}>
                                        <td className={`py-1 font-mono text-xs ${isNonPayable ? 'line-through' : ''}`}>{entry.invoice_number}</td>
                                        <td className={`py-1 truncate max-w-[120px] ${isNonPayable ? 'line-through' : ''}`}>{entry.vendor_name}</td>
                                        <td className={`py-1 text-right font-medium ${isNonPayable ? 'line-through text-slate-400' : ''}`}>{formatCurrency(entry.amount)}</td>
                                        <td className="py-1 text-center text-xs">{entry.due_date}</td>
                                        <td className="py-1 text-center">
                                          {isNonPayable ? (
                                            <button
                                              onClick={() => handleRestoreAP(entry.id)}
                                              className="text-green-600 hover:text-green-800 text-xs flex items-center gap-1 justify-center"
                                              title="Restore"
                                            >
                                              <RotateCcw className="h-3 w-3" />
                                            </button>
                                          ) : (
                                            <button
                                              onClick={() => handleNonPayable(entry.id)}
                                              className="text-slate-500 hover:text-red-600 text-xs flex items-center gap-1 justify-center"
                                              title="Mark as non-payable"
                                            >
                                              <Ban className="h-3 w-3" />
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                              {data.entries.length > 10 && (
                                <p className="text-xs text-center text-slate-500 mt-2">
                                  +{data.entries.length - 10} more entries
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* AR Tab */}
          <TabsContent value="ar">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5 text-green-600" />
                  All Receivables — grouped by customer
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="ar-grouped-table">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="text-left p-3 w-8"></th>
                        <th className="text-left p-3">Company</th>
                        <th className="text-left p-3">Customer</th>
                        <th className="text-center p-3">Invoices</th>
                        <th className="text-right p-3">Open balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupByPartner(dashboard?.ar?.by_vertical, "customer_name").map((group) => {
                        const colors = VERTICAL_COLORS[group.vertical_id];
                        const isOpen = !!expandedPartners[group.key];
                        return (
                          <React.Fragment key={group.key}>
                            <tr
                              className="border-b hover:bg-slate-50 cursor-pointer"
                              onClick={() => togglePartner(group.key)}
                              data-testid={`ar-partner-row-${group.name}`}
                            >
                              <td className="p-3 text-slate-500">
                                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </td>
                              <td className="p-3">
                                <Badge className={`${colors?.badge} text-white text-xs`}>
                                  {getVerticalName(group.vertical_id)}
                                </Badge>
                              </td>
                              <td className="p-3 font-medium text-slate-800">{group.name}</td>
                              <td className="p-3 text-center text-slate-600">{group.count}</td>
                              <td className={`p-3 text-right font-bold ${group.total < 0 ? "text-rose-600" : "text-slate-900"}`}>
                                {formatCurrency(group.total)}
                              </td>
                            </tr>
                            {isOpen && group.entries.map((entry) => (
                              <tr key={entry.id} className="bg-slate-50/60 border-b text-xs">
                                <td></td>
                                <td className="p-2 pl-6 text-slate-400">↳</td>
                                <td className="p-2">
                                  <div className="font-mono text-[11px] text-slate-700">{entry.invoice_number}</div>
                                  {entry.reference && (
                                    <div className="text-[10px] text-slate-400">ref {entry.reference}</div>
                                  )}
                                </td>
                                <td className="p-2 text-center text-slate-500">
                                  {entry.issue_date || "-"} · due {entry.due_date || "-"}
                                </td>
                                <td className={`p-2 text-right font-semibold ${Number(entry.amount) < 0 ? "text-rose-600" : "text-slate-800"}`}>
                                  {formatCurrency(entry.amount)}
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                      {/* Grand total footer */}
                      {dashboard?.ar?.totals?.count > 0 && (
                        <tr className="bg-emerald-50 border-t-2 border-emerald-300 font-bold">
                          <td colSpan={3} className="p-3 text-emerald-800">SOA total</td>
                          <td className="p-3 text-center text-emerald-800">{dashboard.ar.totals.count}</td>
                          <td className="p-3 text-right text-emerald-800" data-testid="ar-grand-total">
                            {formatCurrency((dashboard.ar.totals.open || 0) + (dashboard.ar.totals.overdue || 0))}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {Object.keys(dashboard?.ar?.by_vertical || {}).length === 0 && (
                    <p className="text-center py-8 text-slate-500">
                      No AR entries. Import data from the Import/Export page.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AP Tab */}
          <TabsContent value="ap">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-orange-600" />
                  All Payables — grouped by vendor
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="ap-grouped-table">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="text-left p-3 w-8"></th>
                        <th className="text-left p-3">Company</th>
                        <th className="text-left p-3">Vendor</th>
                        <th className="text-center p-3">Invoices</th>
                        <th className="text-right p-3">Open balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupByPartner(dashboard?.ap?.by_vertical, "vendor_name").map((group) => {
                        const colors = VERTICAL_COLORS[group.vertical_id];
                        const isOpen = !!expandedPartners[group.key];
                        return (
                          <React.Fragment key={group.key}>
                            <tr
                              className="border-b hover:bg-slate-50 cursor-pointer"
                              onClick={() => togglePartner(group.key)}
                              data-testid={`ap-partner-row-${group.name}`}
                            >
                              <td className="p-3 text-slate-500">
                                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </td>
                              <td className="p-3">
                                <Badge className={`${colors?.badge} text-white text-xs`}>
                                  {getVerticalName(group.vertical_id)}
                                </Badge>
                              </td>
                              <td className="p-3 font-medium text-slate-800">{group.name}</td>
                              <td className="p-3 text-center text-slate-600">{group.count}</td>
                              <td className={`p-3 text-right font-bold ${group.total < 0 ? "text-rose-600" : "text-slate-900"}`}>
                                {formatCurrency(group.total)}
                              </td>
                            </tr>
                            {isOpen && group.entries.map((entry) => (
                              <tr key={entry.id} className="bg-slate-50/60 border-b text-xs">
                                <td></td>
                                <td className="p-2 pl-6 text-slate-400">↳</td>
                                <td className="p-2">
                                  <div className="font-mono text-[11px] text-slate-700">{entry.invoice_number}</div>
                                  {entry.reference && (
                                    <div className="text-[10px] text-slate-400">ref {entry.reference}</div>
                                  )}
                                </td>
                                <td className="p-2 text-center text-slate-500">
                                  {entry.issue_date || "-"} · due {entry.due_date || "-"}
                                </td>
                                <td className={`p-2 text-right font-semibold ${Number(entry.amount) < 0 ? "text-rose-600" : "text-slate-800"}`}>
                                  {formatCurrency(entry.amount)}
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                      {/* Grand total footer */}
                      {dashboard?.ap?.totals?.count > 0 && (
                        <tr className="bg-orange-50 border-t-2 border-orange-300 font-bold">
                          <td colSpan={3} className="p-3 text-orange-800">SOA total</td>
                          <td className="p-3 text-center text-orange-800">{dashboard.ap.totals.count}</td>
                          <td className="p-3 text-right text-orange-800" data-testid="ap-grand-total">
                            {formatCurrency((dashboard.ap.totals.open || 0) + (dashboard.ap.totals.overdue || 0))}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {Object.keys(dashboard?.ap?.by_vertical || {}).length === 0 && (
                    <p className="text-center py-8 text-slate-500">
                      No AP entries. Import data from the Import/Export page.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
